/**
 * Email service for sending ownership transfer invitations
 */

import type { OwnershipInviteEmailData } from '@/types/ownership'

/**
 * Sends an ownership transfer invitation email
 * Calls Cloud Function directly with server-side environment variables
 */
export const sendOwnershipInviteEmail = async (data: OwnershipInviteEmailData): Promise<void> => {
  try {
    // Validate required fields
    const { transferId, companyName, fromUserName, toEmail, token } = data
    if (!transferId || !companyName || !fromUserName || !toEmail || !token) {
      throw new Error('Missing required fields for ownership invitation')
    }

    // Get Cloud Function URL from client-side environment (NEXT_PUBLIC_)
    const cloudFunctionUrl = process.env.NEXT_PUBLIC_FIREBASE_FUNCTIONS_URL
    if (!cloudFunctionUrl) {
      throw new Error('Cloud Function URL not configured')
    }

    // Call the Cloud Function
    const response = await fetch(`${cloudFunctionUrl}/sendOwnershipInvite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to send invitation email: ${response.status} ${response.statusText}`)
    }

    const result = await response.json()
  } catch (error: any) {
    throw new Error(`Failed to send ownership invitation: ${error.message}`)
  }
}

/**
 * Send ownership invitation email with authentication
 * @deprecated Use sendOwnershipInviteEmail instead (auth token no longer needed)
 * @param data - Email data including transfer details
 * @param authToken - Firebase ID token for authentication (ignored)
 * @returns Promise that resolves when email is sent
 */
export const sendOwnershipInviteEmailWithAuth = async (
  data: OwnershipInviteEmailData,
  authToken: string,
): Promise<void> => {
  // Delegate to the main function (auth token no longer used)
  return sendOwnershipInviteEmail(data)
}

/**
 * Gets the accept URL for a transfer token
 */
const getAcceptUrl = (token: string): string => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://rtsc.live' : 'http://localhost:3000'

  return `${baseUrl}/ownership/accept/${token}`
}

/**
 * Example usage of the email service:
 *
 * ```typescript
 * import { sendOwnershipInviteEmail } from '@/services/emailService'
 *
 * try {
 *   await sendOwnershipInviteEmail({
 *     transferId: 'transfer_123',
 *     companyName: 'My Company',
 *     fromUserName: 'John Doe',
 *     toEmail: 'newowner@example.com',
 *     token: 'secure_token_123'
 *   })
 *
 *   toast.success('Invitation sent successfully')
 * } catch (error) {
 *   toast.error('Failed to send invitation')
 * }
 * ```
 *
 * Architecture: Frontend → Cloud Function → Email
 *
 * Security: Uses client-side environment variable (NEXT_PUBLIC_FIREBASE_FUNCTIONS_URL) accessible in browser
 */
